import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const STORAGE_KEY = 'live_viewer_name';

export function LivePage() {
  const [name, setName] = useState('');
  const [inputName, setInputName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) setName(stored);
  }, []);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputName.trim();
    if (!trimmed) return;
    localStorage.setItem(STORAGE_KEY, trimmed);
    setName(trimmed);
    await supabase.from('live_viewers').insert({ name: trimmed });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || sending) return;
    setSending(true);
    await supabase.from('live_chat_messages').insert({ name, message: trimmed });
    setMessage('');
    setSent(true);
    setSending(false);
    setTimeout(() => setSent(false), 3000);
  };

  if (!name) {
    return (
      <div
        className="min-h-screen bg-brand-5 flex items-center justify-center px-4"
        style={{ fontFamily: 'Urbanist, sans-serif' }}
      >
        <div
          className="w-full max-w-sm bg-white rounded-3xl p-8 text-center"
          style={{ boxShadow: '0 12px 50px rgba(41,37,36,0.12)' }}
        >
          <img
            src="/human_potential_os_logo_V2_cropped.png"
            alt="NLP OS"
            className="h-12 object-contain mx-auto mb-6"
          />
          <div className="flex items-center justify-center gap-2 mb-4">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: '#e74c3c', animation: 'livePulse 1.5s ease-in-out infinite' }}
            />
            <span className="text-sm font-bold tracking-wide uppercase" style={{ color: '#e74c3c' }}>
              Live Now
            </span>
          </div>
          <h1 className="text-xl font-bold text-gray-80 mb-1">Welcome to the Live Stream</h1>
          <p className="text-sm text-gray-50 mb-6">Enter your first name to join</p>
          <form onSubmit={handleJoin} className="flex flex-col gap-3">
            <input
              type="text"
              value={inputName}
              onChange={e => setInputName(e.target.value)}
              placeholder="Your first name"
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-20 text-gray-80 bg-brand-5 placeholder-gray-40 focus:outline-none focus:border-brand-40 text-sm"
            />
            <button
              type="submit"
              className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-opacity"
              style={{ background: '#7F9748' }}
            >
              Join Stream
            </button>
          </form>
        </div>
        <style>{`@keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.25} }`}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-5 flex flex-col" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="bg-brand-5 px-4 md:px-8 py-3">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
          <img
            src="/human_potential_os_logo_V2_cropped.png"
            alt="NLP OS"
            className="h-12 object-contain object-left"
          />
          <div className="flex items-center gap-2">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: '#e74c3c', animation: 'livePulse 1.5s ease-in-out infinite' }}
            />
            <span className="text-sm font-bold tracking-wide uppercase" style={{ color: '#e74c3c' }}>
              Live Now
            </span>
          </div>
        </div>
      </header>

      {/* ── Title bar ──────────────────────────────────────────── */}
      <div className="px-4 md:px-8 pt-3 pb-2 max-w-screen-2xl mx-auto w-full flex items-center gap-3">
        <h1
          className="text-gray-80 leading-tight"
          style={{ fontSize: 'clamp(16px, 2vw, 26px)', fontWeight: 700, letterSpacing: '-0.02em' }}
        >
          NLP OS<sup className="text-[0.55em] font-normal align-super">™</sup>{' '}
          <span style={{ color: '#7F9748' }}>Live Stream</span>
        </h1>
      </div>

      {/* ── Video Player (full width) ───────────────────────────── */}
      <section className="px-4 md:px-8 pb-4 max-w-screen-2xl mx-auto w-[90%]">
        <div
          className="w-full rounded-2xl overflow-hidden bg-gray-900"
          style={{ boxShadow: '0 12px 60px rgba(41,37,36,0.2)' }}
        >
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src="https://www.youtube.com/embed/uMbKG_D6Fxo?si=AMT2AN8Pr4mlSZn9"
              title="The Coaching Plateau — Free Live Training · Thursday 9th July · 10:00 AM EDT / 3:00 PM BST"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              style={{ border: 0 }}
            />
            {/* Pre-event overlay — remove when stream goes live */}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950/90 pointer-events-none select-none">
              <p className="text-white text-xl sm:text-2xl font-bold mb-2 text-center px-4">The Coaching Plateau</p>
              <p className="text-gray-300 text-sm sm:text-base mb-1 text-center">Free Live Training</p>
              <p className="text-amber-400 text-sm sm:text-base font-semibold text-center">Thursday 9th July · 10:00 AM EDT · 3:00 PM BST</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Info bar + Chat (below video) ─────────────────────── */}
      <section className="px-4 md:px-8 pb-8 max-w-screen-2xl mx-auto w-[90%]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          {/* Stream info */}
          <div
            className="flex-1 rounded-2xl px-5 py-3.5 flex items-center justify-between gap-4 bg-white border border-gray-20"
            style={{ boxShadow: '0 2px 12px rgba(41,37,36,0.06)' }}
          >
            <div>
              <p className="text-xs tracking-widest uppercase font-semibold mb-0.5 text-gray-40">You are watching</p>
              <p className="text-sm font-bold text-gray-80">
                The Coaching Plateau — Free Live Training
              </p>
              <p className="text-xs mt-0.5 text-gray-50">Thursday 9th July · 10:00 AM EDT · 3:00 PM BST · Watching as <span className="font-semibold text-gray-70">{name}</span></p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: '#e74c3c', animation: 'livePulse 1.5s ease-in-out infinite' }}
              />
              <span className="text-sm font-bold" style={{ color: '#e74c3c' }}>Streaming Live</span>
            </div>
          </div>
        </div>

        {/* Chat panel — full width, horizontal layout */}
        <div
          className="w-full rounded-2xl bg-white border border-gray-20 overflow-hidden"
          style={{ boxShadow: '0 2px 16px rgba(41,37,36,0.07)' }}
        >
          {/* Chat header */}
          <div className="px-5 py-3 border-b border-gray-10 flex items-center gap-3">
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
              style={{ background: 'rgba(231,76,60,0.08)' }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full inline-block"
                style={{ background: '#e74c3c', animation: 'livePulse 1.5s ease-in-out infinite' }}
              />
              <span className="text-xs font-semibold" style={{ color: '#e74c3c' }}>Live</span>
            </div>
            <div>
              <span className="text-sm font-bold text-gray-80">Ask a Question</span>
              <span className="text-xs text-gray-40 ml-2">Your message goes directly to the host</span>
            </div>
            {sent && (
              <span
                className="ml-auto text-xs font-medium px-3 py-1 rounded-full"
                style={{ background: 'rgba(127,151,72,0.12)', color: '#4D5C2F' }}
              >
                Message sent — thank you!
              </span>
            )}
          </div>

          {/* Input row */}
          <form onSubmit={handleSend} className="px-5 py-4 flex items-end gap-3">
            <div className="flex-1">
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e as unknown as React.FormEvent);
                  }
                }}
                placeholder={`Hi ${name} — type your question or comment here…`}
                rows={2}
                className="w-full px-4 py-3 rounded-xl border border-gray-20 text-gray-80 bg-brand-5 placeholder-gray-40 focus:outline-none focus:border-brand-40 text-sm resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={!message.trim() || sending}
              className="flex-shrink-0 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-opacity disabled:opacity-40 h-[46px] flex items-center gap-2"
              style={{ background: '#7F9748' }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              {sending ? 'Sending…' : 'Send'}
            </button>
          </form>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="border-t border-gray-20 bg-brand-5 px-4 md:px-8 py-5 mt-auto">
        <div className="max-w-screen-2xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <img
            src="/human_potential_os_logo_V2_cropped.png"
            alt="NLP OS"
            className="h-8 object-contain object-left opacity-50"
          />
          <p className="text-xs text-gray-40">
            humanpotentialos.com · NLP OS™ · All rights reserved.
          </p>
        </div>
      </footer>

      <style>{`@keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.25} }`}</style>
    </div>
  );
}
